import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  addMemory,
  clearTeacherProfile,
  clearMemories,
  deleteMemory,
  getPersonalization,
  saveTeacherProfile,
  setMemoryEnabled,
  updateMemory,
} from '../../../api/personalizationApi';
import { useToast } from '../../../components/Toast';
import { useConfirm } from '../../../components/common/ConfirmDialog';
import PersonalizationSurvey, {
  PRIMARY_TASK_OPTIONS,
} from './PersonalizationSurvey';
import SchoolAutocomplete from './SchoolAutocomplete';
import { SCHOOL_LEVELS, schoolLevelFromKind } from './schoolLevel';
import SettingsSection from './SettingsSection';
import {
  formatMemoryDate,
  isMemoryVisibilityEnabled,
  latestMemoryUpdate,
} from '../../../utils/chatMemoryVisibility';
import './PersonalizationSettings.css';

export const PROFILE_SECTION_ID = 'teacher-profile';
export const MEMORY_SECTION_ID = 'ai-memory';

const EMPTY_PROFILE = {
  school_name: '',
  school_level: '',
  // 나이스 연동(서버가 채움). 비어 있으면 직접 입력한 학교.
  school_code: '',
  office_code: '',
  school_kind: '',
  school_address: '',
  subjects: [],
  grades: [],
  roles: [],
  primary_tasks: [],
  response_style: '',
};

export { schoolLevelFromKind };

const SCOPE_LABELS = {
  global: '전체',
  record: '생기부',
  lesson: '수업 자료',
  assessment: '평가·채점',
  document: '학교 문서',
  communication: '학부모 소통',
  counseling: '상담',
  ppt: 'PPT',
  image: '이미지',
};

// 기억이 어떻게 들어왔는지 — 대화 중 제안 카드로 확정한 기억을 구분해 보여준다.
export const MEMORY_SOURCE_LABELS = {
  proposal: '대화 중 제안으로 저장',
  explicit: '대화에서 요청',
  auto: '자동 저장',
  manual: '직접 추가',
};

const normalizePayload = (payload = {}) => ({
  feature_enabled: Boolean(payload.feature_enabled),
  teacher_profile: {
    ...EMPTY_PROFILE,
    ...(payload.teacher_profile || {}),
  },
  settings: {
    memory_enabled: payload.settings?.memory_enabled !== false,
    profile_survey_completed: Boolean(
      payload.settings?.profile_survey_completed
    ),
    profile_survey_version: Number(
      payload.settings?.profile_survey_version || 0
    ),
  },
  memories: Array.isArray(payload.memories) ? payload.memories : [],
  memory_count: Number(payload.memory_count || 0),
  active_memory_count: Number(
    payload.active_memory_count
    ?? payload.memory_count
    ?? 0
  ),
});

const toInputValue = (values) => (
  Array.isArray(values) ? values.join(', ') : ''
);

const parseList = (value) => (
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
);

/**
 * AI 맞춤 설정 허브의 "전역 층": 선생님 정보·답변 스타일 + AI가 기억하는 내용.
 *
 * 두 섹션은 같은 personalization 문서를 공유하므로 한 컴포넌트가 데이터를 갖고
 * SettingsSection 두 장으로 렌더한다. 접힘 상태는 부모(허브 페이지)가 해시 딥링크와
 * 함께 제어하고, 부모가 넘기지 않으면 첫 섹션만 열린 내부 상태로 동작한다.
 */
const PersonalizationSettings = ({
  currentUser,
  openSections: controlledOpenSections,
  onToggleSection,
}) => {
  const { showToast } = useToast();
  const requestConfirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unsupported, setUnsupported] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [profileForm, setProfileForm] = useState({
    school_name: '',
    school_level: '',
    school_code: '',
    office_code: '',
    school_kind: '',
    school_address: '',
    subjects: '',
    grades: '',
    roles: '',
    primary_tasks: [],
    response_style: '',
  });
  const [newMemory, setNewMemory] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [showAllMemories, setShowAllMemories] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [routeRetry, setRouteRetry] = useState(0);
  const [internalOpen, setInternalOpen] = useState({
    [PROFILE_SECTION_ID]: true,
  });
  const routeRetryTimerRef = useRef(null);
  const routeRetryAttemptsRef = useRef(0);

  const openSections = controlledOpenSections || internalOpen;
  const toggleSection = useCallback((sectionId) => {
    if (onToggleSection) {
      onToggleSection(sectionId);
      return;
    }
    setInternalOpen((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, [onToggleSection]);

  const syncPayload = useCallback((payload, preserveProfileDraft = false) => {
    const normalized = normalizePayload(payload);
    if (routeRetryTimerRef.current) {
      window.clearTimeout(routeRetryTimerRef.current);
      routeRetryTimerRef.current = null;
    }
    routeRetryAttemptsRef.current = 0;
    setUnsupported(false);
    setData(normalized);
    if (!preserveProfileDraft) {
      setProfileForm({
        school_name: normalized.teacher_profile.school_name || '',
        school_level: normalized.teacher_profile.school_level || '',
        school_code: normalized.teacher_profile.school_code || '',
        office_code: normalized.teacher_profile.office_code || '',
        school_kind: normalized.teacher_profile.school_kind || '',
        school_address: normalized.teacher_profile.school_address || '',
        subjects: toInputValue(normalized.teacher_profile.subjects),
        grades: toInputValue(normalized.teacher_profile.grades),
        roles: toInputValue(normalized.teacher_profile.roles),
        primary_tasks: normalized.teacher_profile.primary_tasks || [],
        response_style: normalized.teacher_profile.response_style || '',
      });
    }
  }, []);

  const handleLoadError = useCallback((requestError) => {
    if (requestError?.status === 404) {
      // Frontend/backend rolling deploy 중 구 backend에는 아직 route가 없다.
      // 사용자에게 깨진 설정을 보여주지 않고 새 backend가 올라올 때까지 숨긴다.
      setUnsupported(true);
      setData(null);
      setError('');
      if (
        !routeRetryTimerRef.current
        && routeRetryAttemptsRef.current < 3
      ) {
        const attempt = routeRetryAttemptsRef.current;
        routeRetryAttemptsRef.current += 1;
        const delay = Math.min(
          15_000 * (2 ** attempt) + Math.floor(Math.random() * 5_000),
          90_000
        );
        routeRetryTimerRef.current = window.setTimeout(() => {
          routeRetryTimerRef.current = null;
          setRouteRetry((value) => value + 1);
        }, delay);
      }
      return;
    }
    setError(requestError.message);
  }, []);

  const load = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    setUnsupported(false);
    try {
      syncPayload(await getPersonalization(currentUser));
    } catch (requestError) {
      handleLoadError(requestError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError('');
    getPersonalization(currentUser)
      .then((payload) => {
        if (!cancelled) syncPayload(payload);
      })
      .catch((requestError) => {
        if (!cancelled) handleLoadError(requestError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, handleLoadError, routeRetry, syncPayload]);

  useEffect(() => () => {
    if (routeRetryTimerRef.current) {
      window.clearTimeout(routeRetryTimerRef.current);
    }
  }, []);

  const visibleMemories = useMemo(() => {
    const memories = data?.memories || [];
    return showAllMemories ? memories : memories.slice(0, 6);
  }, [data?.memories, showAllMemories]);
  const memoryLastUpdated = useMemo(() => latestMemoryUpdate(data?.memories), [data?.memories]);

  if (unsupported) {
    return null;
  }

  if (loading) {
    return (
      <div aria-label="AI 맞춤 설정 불러오는 중" role="status">
        <div className="csettings-skeleton" />
        <div className="csettings-skeleton" />
      </div>
    );
  }

  if (!data && error) {
    return (
      <section className="csettings-section is-open personalization-error-card">
        <div className="csettings-section__body">
          <p className="personalization-error" role="alert">{error}</p>
          <button
            className="personalization-btn personalization-btn-outline"
            onClick={load}
            type="button"
          >
            다시 불러오기
          </button>
        </div>
      </section>
    );
  }

  if (!data?.feature_enabled) {
    return null;
  }

  const memoryEnabled = data.settings.memory_enabled;
  const surveyCompleted = data.settings.profile_survey_completed;
  const linkedSchool = profileForm.school_code && profileForm.office_code
    ? {
      school_code: profileForm.school_code,
      office_code: profileForm.office_code,
      school_kind: profileForm.school_kind,
      school_address: profileForm.school_address,
    }
    : null;

  const handleToggle = async () => {
    if (busyAction) return;
    const nextEnabled = !memoryEnabled;
    const previous = data;
    setBusyAction('toggle');
    setError('');
    setData({
      ...data,
      settings: {
        ...data.settings,
        memory_enabled: nextEnabled,
      },
    });
    try {
      syncPayload(
        await setMemoryEnabled(currentUser, nextEnabled),
        true
      );
      showToast(
        nextEnabled ? 'AI 맞춤 답변을 켰어요' : 'AI 맞춤 답변을 껐어요',
        'success'
      );
    } catch (requestError) {
      setData(previous);
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (busyAction) return;
    setBusyAction('profile');
    setError('');
    try {
      const payload = await saveTeacherProfile(currentUser, {
        school_name: profileForm.school_name,
        school_level: profileForm.school_level,
        school_code: profileForm.school_code,
        office_code: profileForm.office_code,
        subjects: parseList(profileForm.subjects),
        grades: parseList(profileForm.grades),
        roles: parseList(profileForm.roles),
        primary_tasks: profileForm.primary_tasks,
        response_style: profileForm.response_style,
      });
      syncPayload(payload);
      showToast('선생님 정보를 저장했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleSurveyComplete = (payload) => {
    syncPayload(payload);
    setSurveyOpen(false);
    showToast('AI 맞춤 설정을 저장했어요', 'success');
  };

  const handleAddMemory = async (event) => {
    event.preventDefault();
    const content = newMemory.trim();
    if (!content || busyAction) return;
    setBusyAction('add-memory');
    setError('');
    try {
      syncPayload(await addMemory(currentUser, content), true);
      setNewMemory('');
      showToast('AI 기억에 추가했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const beginEdit = (memory) => {
    setEditingId(memory.id);
    setEditingContent(memory.content);
    setError('');
  };

  const handleUpdateMemory = async (memoryId) => {
    const content = editingContent.trim();
    if (!content || busyAction) return;
    setBusyAction(`edit-${memoryId}`);
    setError('');
    try {
      syncPayload(
        await updateMemory(currentUser, memoryId, content),
        true
      );
      setEditingId('');
      setEditingContent('');
      showToast('AI 기억을 수정했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    if (busyAction) return;
    setBusyAction(`delete-${memoryId}`);
    setError('');
    try {
      syncPayload(await deleteMemory(currentUser, memoryId), true);
      showToast('AI 기억에서 삭제했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleClearProfile = async () => {
    if (busyAction) return;
    const confirmed = await requestConfirm({
      title: '저장된 선생님 정보를 모두 삭제할까요?',
      description: 'AI 기억은 그대로 유지돼요',
      confirmLabel: '모두 삭제하기',
      danger: true,
    });
    if (!confirmed) return;
    setBusyAction('clear-profile');
    setError('');
    try {
      syncPayload(await clearTeacherProfile(currentUser));
      showToast('선생님 정보를 삭제했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleClearMemories = async () => {
    if (busyAction || data.memories.length === 0) return;
    const confirmed = await requestConfirm({
      title: '저장된 AI 기억을 모두 삭제할까요?',
      description: '선생님 정보는 그대로 유지돼요',
      confirmLabel: '모두 삭제하기',
      danger: true,
    });
    if (!confirmed) return;
    setBusyAction('clear');
    setError('');
    try {
      syncPayload(await clearMemories(currentUser), true);
      showToast('저장된 AI 기억을 모두 삭제했어요', 'success');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyAction('');
    }
  };

  const statusCaption = !surveyCompleted
    ? '몇 가지만 알려주면 예시와 추천이 더 정확해져요'
    : (
      memoryEnabled
        ? `${data.active_memory_count}개의 선호를 기억하고 있어요`
        : '저장된 정보는 유지되며 AI 답변에 사용하지 않아요'
    );

  return (
    <div className="personalization" id="ai-personalization">
      <div className="csettings-master-row">
        <div>
          <div className="csettings-master-title">AI 맞춤 답변</div>
          <div className="csettings-master-caption">{statusCaption}</div>
        </div>
        <button
          aria-checked={memoryEnabled}
          aria-label="AI 맞춤 답변 사용"
          className={`personalization-switch ${memoryEnabled ? 'is-on' : ''}`}
          disabled={busyAction === 'toggle'}
          onClick={handleToggle}
          role="switch"
          type="button"
        >
          <span className="personalization-switch-knob" />
        </button>
      </div>

      <SettingsSection
        description="학교급·과목·답변 방식. 모든 대화에 자동으로 반영돼요"
        id={PROFILE_SECTION_ID}
        onToggle={toggleSection}
        open={Boolean(openSections[PROFILE_SECTION_ID])}
        title="선생님 정보 · 답변 스타일"
      >
        {!surveyCompleted && (
          <button
            className="personalization-survey-panel"
            disabled={Boolean(busyAction)}
            onClick={() => {
              if (!busyAction) setSurveyOpen(true);
            }}
            type="button"
          >
            <span>
              <strong>30초 맞춤 설정</strong>
              <small>몇 가지만 고르면 선생님 정보를 쉽게 채울 수 있어요.</small>
            </span>
            <svg
              aria-hidden="true"
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
            >
              <path
                d="m9 18 6-6-6-6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        )}
        <form
          className="personalization-profile-form"
          onSubmit={handleSaveProfile}
        >
          <div className="personalization-subheading">
            <h3>선생님 정보</h3>
            <p>입력한 정보는 더 알맞은 예시와 표현을 고르는 데만 사용해요.</p>
          </div>
          <div className="personalization-grid">
            <label>
              <span>학교 이름 <small>선택</small></span>
              <SchoolAutocomplete
                currentUser={currentUser}
                disabled={busyAction === 'profile'}
                linked={linkedSchool}
                onChangeName={(schoolName) => setProfileForm((prev) => ({
                  ...prev,
                  school_name: schoolName,
                }))}
                onSelect={(school) => setProfileForm((prev) => ({
                  ...prev,
                  school_name: school.school_name,
                  school_code: school.school_code,
                  office_code: school.office_code,
                  school_kind: school.school_kind || '',
                  school_address: school.school_address || '',
                  // 고른 학교의 종류로 학교급을 자동으로 채운다.
                  school_level: schoolLevelFromKind(school.school_kind) || prev.school_level,
                }))}
                onUnlink={() => setProfileForm((prev) => ({
                  ...prev,
                  school_code: '',
                  office_code: '',
                  school_kind: '',
                  school_address: '',
                }))}
                value={profileForm.school_name}
              />
            </label>
            <label>
              <span>학교급</span>
              <select
                disabled={busyAction === 'profile'}
                onChange={(event) => setProfileForm({
                  ...profileForm,
                  school_level: event.target.value,
                })}
                value={profileForm.school_level}
              >
                {SCHOOL_LEVELS.map((level) => (
                  <option key={level || 'empty'} value={level}>
                    {level || '선택 안 함'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>담당 학년</span>
              <input
                disabled={busyAction === 'profile'}
                maxLength={120}
                onChange={(event) => setProfileForm({
                  ...profileForm,
                  grades: event.target.value,
                })}
                placeholder="예: 2학년, 3학년"
                value={profileForm.grades}
              />
            </label>
            <label>
              <span>담당 과목</span>
              <input
                disabled={busyAction === 'profile'}
                maxLength={160}
                onChange={(event) => setProfileForm({
                  ...profileForm,
                  subjects: event.target.value,
                })}
                placeholder="예: 사회, 역사"
                value={profileForm.subjects}
              />
            </label>
            <label>
              <span>담당 역할</span>
              <input
                disabled={busyAction === 'profile'}
                maxLength={160}
                onChange={(event) => setProfileForm({
                  ...profileForm,
                  roles: event.target.value,
                })}
                placeholder="예: 담임, 연구부"
                value={profileForm.roles}
              />
            </label>
            <label>
              <span>선호 답변 방식</span>
              <select
                disabled={busyAction === 'profile'}
                onChange={(event) => setProfileForm({
                  ...profileForm,
                  response_style: event.target.value,
                })}
                value={profileForm.response_style}
              >
                <option value="">자동으로 판단</option>
                <option value="concise">핵심부터 간결하게</option>
                <option value="practical">바로 쓸 예시까지</option>
                <option value="options">선택지를 비교해서</option>
              </select>
            </label>
          </div>
          <div className="personalization-profile-tasks">
            <span>자주 사용하는 업무</span>
            <div>
              {profileForm.primary_tasks.length > 0 ? (
                profileForm.primary_tasks.map((task) => (
                  <span className="personalization-profile-task" key={task}>
                    {PRIMARY_TASK_OPTIONS.find((item) => item.value === task)?.label || task}
                  </span>
                ))
              ) : (
                <span className="personalization-profile-task-empty">아직 선택하지 않았어요</span>
              )}
            </div>
            {surveyCompleted && (
              <button
                className="personalization-resurvey"
                disabled={Boolean(busyAction)}
                onClick={() => {
                  if (!busyAction) setSurveyOpen(true);
                }}
                type="button"
              >
                맞춤 질문 다시 하기
              </button>
            )}
          </div>
          <div className="personalization-profile-actions">
            <button
              className="personalization-btn personalization-btn-primary personalization-save"
              disabled={busyAction === 'profile'}
              type="submit"
            >
              {busyAction === 'profile' ? '저장 중...' : '정보 저장하기'}
            </button>
            <button
              className="personalization-clear-profile"
              disabled={Boolean(busyAction)}
              onClick={handleClearProfile}
              type="button"
            >
              선생님 정보 지우기
            </button>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection
        badge={data.memories.length > 0 ? data.memories.length : undefined}
        description="대화에서 알아낸 선호. 학생 이름·성적·상담 내용은 기억하지 않아요"
        id={MEMORY_SECTION_ID}
        onToggle={toggleSection}
        open={Boolean(openSections[MEMORY_SECTION_ID])}
        title="AI가 기억하는 내용"
      >
        <div
          className="personalization-memory-section"
          id="ai-memory-settings"
        >
          {isMemoryVisibilityEnabled() && (
            <p className="personalization-memory-summary" data-testid="memory-summary">
              {memoryEnabled
                ? [
                  `기억 ${data.active_memory_count}개`,
                  memoryLastUpdated ? `마지막 업데이트 ${formatMemoryDate(memoryLastUpdated)}` : '',
                ].filter(Boolean).join(' · ')
                : `AI 맞춤 답변이 꺼져 있어 새로 기억하지 않아요 · 저장된 기억 ${data.active_memory_count}개`}
            </p>
          )}
          <form
            className="personalization-add-memory"
            onSubmit={handleAddMemory}
          >
            <label className="personalization-visually-hidden" htmlFor="new-ai-memory">
              AI가 기억할 내용
            </label>
            <input
              disabled={!memoryEnabled || busyAction === 'add-memory'}
              id="new-ai-memory"
              maxLength={300}
              onChange={(event) => setNewMemory(event.target.value)}
              placeholder="예: 답변은 핵심부터 간결하게 써줘"
              value={newMemory}
            />
            <button
              className="personalization-btn personalization-btn-outline"
              disabled={!memoryEnabled || !newMemory.trim() || busyAction === 'add-memory'}
              type="submit"
            >
              추가하기
            </button>
          </form>

          {data.memories.length === 0 ? (
            <p className="personalization-empty">
              아직 저장된 기억이 없어요. 대화 중 필요한 선호를 알아서 정리해요.
            </p>
          ) : (
            <ul className="personalization-memory-list">
              {visibleMemories.map((memory) => (
                <li key={memory.id}>
                  {editingId === memory.id ? (
                    <div className="personalization-memory-edit">
                      <label
                        className="personalization-visually-hidden"
                        htmlFor={`memory-${memory.id}`}
                      >
                        기억 내용 수정
                      </label>
                      <input
                        autoFocus
                        id={`memory-${memory.id}`}
                        maxLength={300}
                        onChange={(event) => setEditingContent(event.target.value)}
                        value={editingContent}
                      />
                      <div className="personalization-memory-actions">
                        <button
                          className="personalization-btn personalization-btn-primary"
                          disabled={!editingContent.trim() || Boolean(busyAction)}
                          onClick={() => handleUpdateMemory(memory.id)}
                          type="button"
                        >
                          저장하기
                        </button>
                        <button
                          className="personalization-btn personalization-btn-ghost"
                          disabled={Boolean(busyAction)}
                          onClick={() => setEditingId('')}
                          type="button"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="personalization-memory-copy">
                        <span className="personalization-scope">
                          {memory.expired
                            ? '만료됨'
                            : (SCOPE_LABELS[memory.scope] || '맞춤')}
                        </span>
                        {MEMORY_SOURCE_LABELS[memory.source] && (
                          <span className="personalization-memory-source">
                            {MEMORY_SOURCE_LABELS[memory.source]}
                          </span>
                        )}
                        <p>{memory.content}</p>
                      </div>
                      <div className="personalization-memory-actions">
                        <button
                          className="personalization-text-button"
                          disabled={Boolean(busyAction)}
                          onClick={() => beginEdit(memory)}
                          type="button"
                        >
                          수정
                        </button>
                        <button
                          className="personalization-text-button is-danger"
                          disabled={Boolean(busyAction)}
                          onClick={() => handleDeleteMemory(memory.id)}
                          type="button"
                        >
                          삭제
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {data.memories.length > 6 && (
            <button
              className="personalization-more"
              onClick={() => setShowAllMemories((value) => !value)}
              type="button"
            >
              {showAllMemories
                ? '일부만 보기'
                : `${data.memories.length - 6}개 더 보기`}
            </button>
          )}
          {data.memories.length > 0 && (
            <button
              className="personalization-clear"
              disabled={Boolean(busyAction)}
              onClick={handleClearMemories}
              type="button"
            >
              기억 모두 삭제
            </button>
          )}
        </div>
      </SettingsSection>

      {error && (
        <p className="personalization-error" role="alert">{error}</p>
      )}
      <PersonalizationSurvey
        currentUser={currentUser}
        initialProfile={data.teacher_profile}
        onClose={() => setSurveyOpen(false)}
        onComplete={handleSurveyComplete}
        open={surveyOpen}
      />
    </div>
  );
};

export default PersonalizationSettings;
